import { Button, Card, Chip, Field, Panel } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, type StockItemRow } from "../../api.js";
import { ErrorLine, useAction } from "../common.js";
import { formatMilli, formatQty, parseMilli } from "./format.js";

interface ComponentRow {
  id: string;
  name: string;
}

export function ItemsTab() {
  const items = useQuery({
    queryKey: ["inventory", "items"],
    queryFn: () => api<StockItemRow[]>("/inventory/items"),
  });

  const components = useQuery({
    queryKey: ["menu", "components"],
    queryFn: () => api<ComponentRow[]>("/menu/lists/components").catch(() => []),
  });

  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState<StockItemRow | null>(null);

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("raw");
  const [baseUnit, setBaseUnit] = useState("kg");
  const [minStock, setMinStock] = useState("");
  const [componentId, setComponentId] = useState("");

  const addAction = useAction(async () => {
    const minStockMilli = minStock.trim() ? (parseMilli(minStock) ?? 0) : 0;
    await api("/inventory/items", {
      body: {
        code: code.trim(),
        name: name.trim(),
        category: category.trim(),
        baseUnit: baseUnit.trim(),
        minStockMilli,
        componentId: componentId || null,
        conversions: [],
        status: "active",
      },
    });
    resetForm();
  }, [["inventory", "items"]]);

  const updateAction = useAction(async () => {
    if (!editingItem) return;
    const minStockMilli = minStock.trim() ? (parseMilli(minStock) ?? 0) : 0;
    await api(`/inventory/items/${editingItem.id}?version=${String(editingItem.version)}`, {
      method: "PUT",
      body: {
        code: code.trim(),
        name: name.trim(),
        category: category.trim(),
        baseUnit: baseUnit.trim(),
        minStockMilli,
        componentId: componentId || null,
        conversions: [],
        status: editingItem.status,
        version: editingItem.version,
      },
    });
    resetForm();
  }, [["inventory", "items"]]);

  const archiveAction = useAction(
    async (item: StockItemRow) => {
      const reason = window.prompt(`دلیل غیرفعال‌سازی کالای «${item.name}»؟`);
      if (!reason?.trim()) return;
      await api(`/inventory/items/${item.id}/archive`, {
        body: { version: item.version, reason: reason.trim() },
      });
    },
    [["inventory", "items"]],
  );

  const resetForm = () => {
    setCode("");
    setName("");
    setCategory("raw");
    setBaseUnit("kg");
    setMinStock("");
    setComponentId("");
    setShowAdd(false);
    setEditingItem(null);
  };

  const startEdit = (item: StockItemRow) => {
    setEditingItem(item);
    setCode(item.code);
    setName(item.name);
    setCategory(item.category);
    setBaseUnit(item.baseUnit);
    setMinStock(item.minStockMilli ? formatMilli(item.minStockMilli) : "");
    setComponentId(item.componentId ?? "");
    setShowAdd(true);
  };

  const compsMap = new Map((components.data ?? []).map((c) => [c.id, c.name]));
  const activeItems = (items.data ?? []).filter((i) => i.status === "active");

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      <div className="row wrap justify-between items-center">
        <h2 className="t-card">فهرست اقلام و کالاهای انبار</h2>
        <Button
          variant="default"
          icon="plus"
          onClick={() => {
            if (showAdd) resetForm();
            else setShowAdd(true);
          }}
        >
          {showAdd ? "بستن فرم" : "تعریف کالای جدید"}
        </Button>
      </div>

      {showAdd && (
        <Card>
          <div className="stack" style={{ gap: "var(--s3)" }}>
            <h3 className="t-card">
              {editingItem ? `ویرایش کالای «${editingItem.name}»` : "تعریف کالای جدید"}
            </h3>
            <div className="row wrap" style={{ gap: "var(--s3)" }}>
              <Field
                id="item-code"
                label="کد کالا"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                placeholder="مثلاً RM-RICE-01"
              />
              <Field
                id="item-name"
                label="نام کالا"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="مثلاً برنج طارم محلی"
              />
              <div className="field">
                <label htmlFor="item-cat">دسته‌بندی</label>
                <select
                  id="item-cat"
                  className="input"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                  }}
                >
                  <option value="raw">ماده اولیه (خام)</option>
                  <option value="prep">نیمه‌آماده / فرآوری‌شده</option>
                  <option value="pack">بسته‌بندی و ظروف</option>
                  <option value="consumable">مصرفی آشپزخانه</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="item-unit">واحد پایه شمارش</label>
                <select
                  id="item-unit"
                  className="input"
                  value={baseUnit}
                  onChange={(e) => {
                    setBaseUnit(e.target.value);
                  }}
                >
                  <option value="kg">کیلوگرم (kg)</option>
                  <option value="g">گرم (g)</option>
                  <option value="lit">لیتر (lit)</option>
                  <option value="portion">پرس (portion)</option>
                  <option value="pcs">عدد / قوطی (pcs)</option>
                  <option value="box">بسته / جعبه (box)</option>
                </select>
              </div>
              <Field
                id="item-min"
                label="حداقل موجودی هشدار"
                value={minStock}
                onChange={(e) => {
                  setMinStock(e.target.value);
                }}
                placeholder="مثلاً ۱۰ یا ۲٫۵"
              />
              <div className="field">
                <label htmlFor="item-comp">اتصال به جزء منو (اختیاری)</label>
                <select
                  id="item-comp"
                  className="input"
                  value={componentId}
                  onChange={(e) => {
                    setComponentId(e.target.value);
                  }}
                >
                  <option value="">بدون اتصال (کالای عمومی / ماده اولیه پایه)</option>
                  {(components.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <ErrorLine error={editingItem ? updateAction.error : addAction.error} />

            <div className="row" style={{ gap: "var(--s2)" }}>
              <Button
                variant="primary"
                disabled={!code.trim() || !name.trim() || (editingItem ? updateAction.busy : addAction.busy)}
                onClick={() => {
                  if (editingItem) updateAction.run(undefined);
                  else addAction.run(undefined);
                }}
              >
                {editingItem ? "ذخیره تغییرات" : "ثبت کالا"}
              </Button>
              <Button variant="ghost" onClick={resetForm}>
                انصراف
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Panel title="کالاهای فعال">
        <ErrorLine error={archiveAction.error} />
        {items.isLoading ? (
          <p className="hint">در حال بارگذاری کالاها...</p>
        ) : activeItems.length === 0 ? (
          <p className="hint">هنوز کالایی تعریف نشده است.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>کد کالا</th>
                  <th>نام کالا</th>
                  <th>دسته‌بندی</th>
                  <th>واحد پایه</th>
                  <th>حداقل هشدار</th>
                  <th>جزء منو مرتبط</th>
                  <th>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.map((item) => {
                  const compName = item.componentId ? compsMap.get(item.componentId) : null;
                  return (
                    <tr key={item.id}>
                      <td>{item.code}</td>
                      <td>{item.name}</td>
                      <td>
                        <Chip plain tone="dark">
                          {item.category === "raw"
                            ? "ماده اولیه"
                            : item.category === "prep"
                              ? "نیمه‌آماده"
                              : item.category === "pack"
                                ? "بسته‌بندی"
                                : item.category}
                        </Chip>
                      </td>
                      <td>{item.baseUnit}</td>
                      <td className="num">
                        {item.minStockMilli ? formatQty(item.minStockMilli, item.baseUnit) : "—"}
                      </td>
                      <td>{compName ? <Chip tone="blue">{compName}</Chip> : "—"}</td>
                      <td>
                        <div className="row" style={{ gap: "var(--s1)" }}>
                          <Button
                            variant="ghost"
                            size="md"
                            onClick={() => {
                              startEdit(item);
                            }}
                          >
                            ویرایش
                          </Button>
                          <Button
                            variant="ghost"
                            size="md"
                            onClick={() => {
                              archiveAction.run(item);
                            }}
                          >
                            غیرفعال
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
